import { useState } from 'react'
import styled from 'styled-components'
import { IconCheck, IconCopy } from 'suomifi-icons'

/**
 * UID + kopiointi-ikoni. Klikkaus kopioi UID:n leikepöydälle ja näyttää
 * hetken vahvistuksen. event.stopPropagation pysäyttää klikin leviämisen
 * → rivi ei avaudu tukipyyntölistalla, kun käyttäjä kopioi UID:n
 * (workshop B8/B9, UX-tutkimuksen suositus).
 *
 * Tabletin näyttöä varten ikoni on aina näkyvissä, ei hover-aktiivinen.
 *
 * `todennettu`-prop: jos annettu, renderöidään pieni dot ennen UID:tä
 * (vihreä = vahvistettu, harmaa = ei). Yhtenäinen B1-tyyli koko
 * portaalissa (workshop 1.6.2026).
 */

interface Props {
  uid: string
  todennettu?: boolean
}

const Wrap = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xxs};
`

const VahvistusDot = styled.span<{ $todennettu: boolean }>`
  display: inline-block;
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background: ${({ theme, $todennettu }) =>
    $todennettu ? theme.colors.successBase : theme.colors.depthBase};
  flex-shrink: 0;
`

const UidText = styled.span`
  font-family: 'Courier New', monospace;
  font-size: 0.8125rem;
`

const CopyButton = styled.button`
  background: transparent;
  border: 0;
  padding: ${({ theme }) => theme.spacing.xxs};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.depthDark1};
  border-radius: 2px;

  &:hover {
    background: ${({ theme }) => theme.colors.depthLight3};
    color: ${({ theme }) => theme.colors.highlightBase};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.highlightBase};
    outline-offset: 1px;
  }

  & > svg {
    width: 0.875rem;
    height: 0.875rem;
  }
`

const Confirmed = styled.span`
  color: ${({ theme }) => theme.colors.successDark1};
  font-size: 0.6875rem;
  font-weight: 600;
`

export default function UidCopy({ uid, todennettu }: Props) {
  const [kopioitu, setKopioitu] = useState(false)

  function handleCopy(e: React.MouseEvent) {
    e.stopPropagation()
    e.preventDefault()
    navigator.clipboard.writeText(uid).then(() => {
      setKopioitu(true)
      setTimeout(() => setKopioitu(false), 1500)
    })
  }

  return (
    <Wrap>
      {todennettu !== undefined && (
        <VahvistusDot $todennettu={todennettu} aria-hidden />
      )}
      <UidText>{uid}</UidText>
      <CopyButton
        type="button"
        onClick={handleCopy}
        aria-label={`Kopioi UID ${uid}`}
        title="Kopioi UID"
      >
        {kopioitu ? <IconCheck aria-hidden /> : <IconCopy aria-hidden />}
      </CopyButton>
      {kopioitu && <Confirmed>Kopioitu</Confirmed>}
    </Wrap>
  )
}
